import React from 'react';
import PageScrollTable from '../ui/PageScrollTable';
import Dropdown from '../ui/Dropdown';
import { PHONE_SECTION_BODY, PHONE_FLOAT_ITEM, PHONE_GROUP_WRAPPER, PHONE_GROUP_HEADER } from '../../constants/phoneLayout';
import { GroupHeader } from '../ui/GroupedList';
import TaskStatusBadge from '../teams/TaskStatusBadge';
import DueIndicator from '../DueIndicator';
import { dueStatus } from '../../utils/dueStatus';
import { formatDateCompact } from '../../utils/dateFormat';
import { ROW_HOVER } from '../../constants/rowStyles';
import { TAP_TARGET_PHONE } from '../../constants/buttons';

/**
 * THE task table — ONE renderer for every place a task is listed (Gags/teams/
 * tasks Stage 1, owner sign-off 2026-10-02, spec "Reuse plan": *"ONE task
 * table: column definitions + a desktop row + a phone two-line row, on
 * ui/PageScrollTable (the order-table pattern: ORDER_TABLE_COLUMNS /
 * OrderMobileTableRow). Used by My work, Given by me and the gag panel's task
 * list"*). It replaced `teams/MyTasksRows` (My Tasks) and the List view of
 * `teams/TeamTaskBoard` (`teams/TaskListView` / `teams/TaskCard`) — the same
 * task drawn two ways, where a fix to one missed the other.
 *
 * 🔑 IT DECIDES NOTHING ABOUT WHO MAY DO WHAT. The caller passes finished,
 * already-gated pieces: `renderExpanded(task)` (the editor, read-only or not),
 * `rowMenu(task)` (the phone ⋯ items, each already gated), `isFaded(task)`.
 *
 * ♿ FADED ("everyone else's task", My work's Gag grouping) is MUTED TEXT that
 * still passes contrast — `text-gray-500` (#6b7280 on white ≈ 4.8:1; dark
 * `text-gray-400` #9ca3af on #1f2937 ≈ 6.0:1) — never `opacity`, which would
 * also fade the status badge below its own contrast (spec: "muted text that
 * still passes contrast; never opacity below it").
 *
 * Columns are `ui/PageScrollTable`'s shape — a `min` on every one.
 */
export const TASK_TABLE_COLUMN_KEYS = Object.freeze(['expand', 'task', 'context', 'assignees', 'due', 'status']);

export function taskTableColumns({ hide = [], contextLabel = 'Gag / From' } = {}) {
  const all = [
    { key: 'expand', label: '', ariaLabel: 'Expand', width: 44, min: 44 },
    // 📏 Widths are the table's FLOOR (PageScrollTable sums them) and stretch
    // to fill. Summed 994 until 2026-10-02 — wider than My Tasks' ~976px of
    // content at 1280 with the sidebar open, so Given by me scrolled sideways
    // (owner). Now 894: every column still clears its own header and data.
    { key: 'task', label: 'Task', width: 280, min: 180, sortField: 'title' },
    { key: 'context', label: contextLabel, width: 180, min: 120, sortField: 'context' },
    { key: 'assignees', label: 'Assigned to', width: 160, min: 120, sortField: 'assignees' },
    { key: 'due', label: 'Due', width: 110, min: 100, sortField: 'due_date' },
    { key: 'status', label: 'Status', width: 120, min: 110, sortField: 'status' },
  ];
  return all.filter((c) => !hide.includes(c.key));
}

const STATUS_ORDER = ['todo', 'in_progress', 'blocked', 'done', 'cancelled'];

/**
 * THE header click for a task table: the same column flips the direction, a
 * new column starts ascending. One rule for every caller (the gag panel's
 * board and My Tasks), so the two cannot answer the same click differently.
 */
export const nextTaskSort = (current, field) => (current.field === field
  ? { field, dir: current.dir === 'asc' ? 'desc' : 'asc' }
  : { field, dir: 'asc' });
export const assigneeNames = (task) => (Array.isArray(task.assignees) ? task.assignees : [])
  .map((a) => a.display_name).filter(Boolean).join(', ');

/** "Sam Carter" → "Sam C" (a one-word name stays as it is). */
const shortName = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  return parts.length > 1 ? `${parts[0]} ${parts[parts.length - 1][0]}` : (parts[0] || '');
};

/**
 * 📱 The phone row's line-3 people (owner 2026-10-03: "Me, Sam B, Sarah C,
 * +2 others"): "Me" first when the viewer is assigned, then up to two short
 * names, then "+N others". Identity is the USER ID, never the name.
 * Empty → "Unassigned".
 */
export const assigneeShortLine = (task, viewerUserId) => {
  const list = (Array.isArray(task.assignees) ? task.assignees : []).filter((a) => a && a.display_name);
  if (list.length === 0) return 'Unassigned';
  const me = viewerUserId != null && viewerUserId !== ''
    ? list.find((a) => String(a.user_id ?? a.id) === String(viewerUserId)) : null;
  const others = list.filter((a) => a !== me);
  const shown = others.slice(0, 2).map((a) => shortName(a.display_name));
  const rest = others.length - shown.length;
  const parts = [...(me ? ['Me'] : []), ...shown];
  if (rest > 0) parts.push(`+${rest} other${rest === 1 ? '' : 's'}`);
  return parts.join(', ');
};

/**
 * The flat list's header sort — blanks LAST in both directions for dates and
 * text; status in workflow order. `contextText(task)` supplies the Gag / From
 * column's text (the page knows how it reads).
 */
export function sortTasks(tasks, field, dir, contextText = () => '') {
  const sign = dir === 'desc' ? -1 : 1;
  const text = (t) => {
    if (field === 'title') return t.title || '';
    if (field === 'context') return contextText(t) || '';
    if (field === 'assignees') return assigneeNames(t);
    if (field === 'due_date') return t.due_date || '';
    return '';
  };
  return [...tasks].sort((a, b) => {
    if (field === 'status') {
      return sign * (STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status));
    }
    const x = text(a);
    const y = text(b);
    if (!x && !y) return 0;
    if (!x) return 1;
    if (!y) return -1;
    return sign * x.localeCompare(y, undefined, { sensitivity: 'base' });
  });
}

function DueCell({ task }) {
  if (!task.due_date) return <span className="text-gray-400 dark:text-gray-500">No date</span>;
  const due = dueStatus(task.due_date, task.status);
  const tone = due === 'overdue' ? 'text-red-700 dark:text-red-300 font-semibold'
    : due === 'due_soon' ? 'text-amber-700 dark:text-amber-300 font-semibold' : '';
  return (
    <span className={`inline-flex items-center gap-1 ${tone}`}>
      <DueIndicator state={due} />
      {formatDateCompact(task.due_date)}
    </span>
  );
}

const TD = 'px-2 py-2 align-top';
const titleTone = (faded) => (faded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-800 dark:text-gray-200');
const textTone = (faded) => (faded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-300');

function ContextCell({ ctx, faded }) {
  if (!ctx || !ctx.text) return <span className="text-gray-400 dark:text-gray-500">—</span>;
  if (ctx.onClick) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); ctx.onClick(); }}
        title={ctx.title}
        className={`text-left hover:underline whitespace-normal break-words ${faded ? 'text-gray-500 dark:text-gray-400' : 'text-indigo-700 dark:text-indigo-300'}`}
      >
        {ctx.text}
      </button>
    );
  }
  return <span className={`whitespace-normal break-words ${textTone(faded)}`}>{ctx.text}</span>;
}

/** Desktop row (≥ md). The whole row toggles; the chevron is the keyboard control. */
function TaskTableRow({ task, columns, expanded, onToggle, renderExpanded, faded, ctx, testIdPrefix }) {
  const cells = {
    expand: (
      <td key="expand" className={TD}>
        <button
          type="button"
          aria-expanded={expanded}
          aria-label={expanded ? 'Collapse task' : 'Expand task'}
          onClick={(e) => { e.stopPropagation(); onToggle(task); }}
          className="inline-flex items-center justify-center w-6 h-6 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
        >
          <span className={`text-gray-400 dark:text-gray-500 text-xs transition-transform ${expanded ? 'rotate-90' : ''}`}>&#9654;</span>
        </button>
      </td>
    ),
    task: (
      <td key="task" className={TD}>
        <span className={`block text-sm font-medium whitespace-normal break-words ${titleTone(faded)}`} data-testid={`${testIdPrefix}-title-${task.id}`}>
          {task.title}
        </span>
      </td>
    ),
    context: <td key="context" className={`${TD} text-sm`}><ContextCell ctx={ctx} faded={faded} /></td>,
    assignees: (
      <td key="assignees" className={`${TD} text-sm whitespace-normal break-words ${textTone(faded)}`}>
        {assigneeNames(task) || <span className="text-gray-400 dark:text-gray-500">Unassigned</span>}
      </td>
    ),
    due: <td key="due" className={`${TD} text-sm ${textTone(faded)}`}><DueCell task={task} /></td>,
    status: <td key="status" className={TD}><TaskStatusBadge status={task.status} /></td>,
  };
  return (
    <>
      <tr
        className={`cursor-pointer transition-colors ${expanded ? 'bg-gray-50 dark:bg-gray-700/30' : ROW_HOVER}`}
        data-testid={`${testIdPrefix}-row-${task.id}`}
        data-faded={faded ? '' : undefined}
        onClick={() => onToggle(task)}
      >
        {columns.map((c) => cells[c.key])}
      </tr>
      {expanded && (
        <tr data-testid={`${testIdPrefix}-expanded-${task.id}`} data-expanded-panel="">
          <td colSpan={columns.length} className="px-2 pt-1 pb-5 bg-gray-50 dark:bg-gray-700/30">
            {/* The open task is a LIFTED card (owner 2026-10-04: "should have the
                shadow effect … might need more spacing") — the shared
                `--cf-item-lift` shadow the order line items use (index.css). */}
            <div data-item-lift="" className="px-4 pb-4 pt-3 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800">
              {renderExpanded(task)}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * Phone row (< md): TWO lines. Line 1 = title (wraps) + status; line 2 = due ·
 * gag/from · assignees. The row is ONE button (the tap target); the ⋯ menu is
 * its SIBLING, never nested, and opens as a bottom sheet (`ui/Dropdown` below
 * `md`).
 */
export function TaskPhoneRow({ task, expanded, onToggle, renderExpanded, faded, ctx, menuItems, testIdPrefix, lift = false, closing = false }) {
  // 📱 Three lines (owner 2026-10-03), each WRAPPING, never truncated (the owner
  // ruling pinned by mobileBatch.phoneClasses item 15 — a long title wraps):
  // 1 title + status · 2 due · gag/from · 3 people · sub-department.
  const people = assigneeShortLine(task, localStorage.getItem('sfx_user_id'));
  const subDept = task.gag_sub_department_name || task.sub_department_name || '';
  return (
    <div className={`${PHONE_FLOAT_ITEM}${expanded && !lift ? ' my-3' : ''}`} data-testid={`${testIdPrefix}-row-${task.id}`} data-faded={faded ? '' : undefined}
      data-item-lift={expanded || lift ? '' : undefined}>
      <div className="flex items-center">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => onToggle(task)}
          className={`flex-1 min-w-0 text-left pl-2 pr-[5px] py-[5px] min-h-[44px] flex flex-col gap-0.5 transition-colors ${expanded ? 'bg-gray-50 dark:bg-gray-700/30' : ROW_HOVER}`}
        >
          <span className="flex items-start gap-2 w-full">
            <span className={`flex-1 min-w-0 text-sm font-medium break-words ${titleTone(faded)}`} title={task.title} data-testid={`${testIdPrefix}-title-${task.id}`}>
              {task.title}
            </span>
            <TaskStatusBadge status={task.status} className="shrink-0" />
          </span>
          <span className={`block w-full text-xs break-words ${faded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-600 dark:text-gray-300'}`} data-testid={`${testIdPrefix}-line2-${task.id}`}>
            <DueCell task={task} />
            {ctx && ctx.text ? <> · {ctx.text}</> : null}
          </span>
          <span className={`block w-full text-xs break-words ${faded ? 'text-gray-500 dark:text-gray-400' : 'text-gray-500 dark:text-gray-400'}`} data-testid={`${testIdPrefix}-line3-${task.id}`}>
            {people}{subDept ? <> · {subDept}</> : null}
          </span>
        </button>
        {menuItems && menuItems.length > 0 && (
          <Dropdown
            align="right"
            ariaLabel={`Actions for ${task.title}`}
            triggerTestId={`${testIdPrefix}-menu-${task.id}`}
            triggerClassName={`px-3 text-lg leading-none text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 ${TAP_TARGET_PHONE}`}
            label={<span aria-hidden="true">⋯</span>} hideChevron
            panelClassName="w-56 p-1"
          >
            {({ close }) => (
              <ul>
                {menuItems.map((item) => (
                  <li key={item.label}>
                    <button
                      type="button"
                      onClick={() => { close(); item.onSelect(); }}
                      className="w-full text-left px-3 py-2.5 min-h-[44px] text-sm rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      {item.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </Dropdown>
        )}
      </div>
      {expanded && (
        // A CLOSING row (`closingId`, below) is inert: it stays drawn while the opened
        // one scrolls up, but an edit typed into it would be dropped when it closes.
        <div className="px-3 pb-3 pt-2 border-t border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30"
          {...(closing ? { inert: '', 'data-closing': '' } : {})}>
          {renderExpanded(task)}
        </div>
      )}
    </div>
  );
}

/**
 * groups: [{ key, label (null = untitled/flat), summary?, actions?, tasks }].
 * groupState: `ui/GroupedList#useGroupedList()` — omitted, every group is open.
 */
export default function TaskTable({
  groups,
  groupState = null,
  columns,
  isMobile = false,
  stickyTop = null,
  sort = null,
  expandedId = null,
  onToggle,
  renderExpanded,
  isFaded = () => false,
  contextOf = () => null,
  rowMenu = null,
  testIdPrefix = 'task-table',
  editingRowId = null,
  // 📱 Every phone card lifted, 20px apart — the opened order's line items
  // (owner 2026-10-04, Breakdown task planning). Off: My Tasks' tighter list.
  phoneLift = false,
  // 📱 A row kept OPEN beside `expandedId` while the newly opened one scrolls to
  // the top (`hooks/useOpenAtTop.js`); the caller clears it when the scroll ends.
  closingId = null,
}) {
  const isOpen = (g) => (groupState ? groupState.isOpen(g) : true);
  const toggleGroup = (g) => { if (groupState) groupState.toggle(g); };

  if (isMobile) {
    return (
      <div className="space-y-2" data-testid={`${testIdPrefix}-phone`}>
        {groups.map((g) => (
          <div key={g.key} className={PHONE_GROUP_WRAPPER || undefined}
            data-testid={g.label != null ? `${testIdPrefix}-group-${g.key}` : undefined}>
            {g.label != null && (
              <GroupHeader open={isOpen(g)} onToggle={() => toggleGroup(g)} label={g.label} summary={g.summary}
                actions={g.actions} testId={`${testIdPrefix}-group-header-${g.key}`} stacked className={PHONE_GROUP_HEADER} />
            )}
            {isOpen(g) && (
              <div className={phoneLift ? 'space-y-5 pt-1 pb-3' : PHONE_SECTION_BODY}>
                {g.tasks.map((t) => (
                  <TaskPhoneRow key={t.id} task={t} expanded={expandedId === t.id || (closingId != null && closingId === t.id)}
                    closing={closingId != null && closingId === t.id && expandedId !== t.id} onToggle={onToggle}
                    renderExpanded={renderExpanded} faded={isFaded(t)} ctx={contextOf(t)}
                    menuItems={rowMenu ? rowMenu(t) : null} testIdPrefix={testIdPrefix} lift={phoneLift} />
                ))}
                {g.tasks.length === 0 && g.emptyNote && (
                  <div className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{g.emptyNote}</div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  }

  return (
    <PageScrollTable
      testIdPrefix={testIdPrefix}
      columns={columns}
      stickyTop={stickyTop}
      sortField={sort ? sort.field : null}
      sortDir={sort ? sort.dir : 'asc'}
      onHeaderSort={sort ? sort.onSort : null}
      editingRowId={editingRowId}
    >
      {groups.flatMap((g) => [
        g.label != null ? (
          <tr key={`h-${g.key}`} data-testid={`${testIdPrefix}-group-${g.key}`}>
            <td colSpan={columns.length} className="p-0 border-t border-gray-200 dark:border-gray-700">
              <GroupHeader open={isOpen(g)} onToggle={() => toggleGroup(g)} label={g.label} summary={g.summary}
                actions={g.actions} testId={`${testIdPrefix}-group-header-${g.key}`} />
            </td>
          </tr>
        ) : null,
        ...(isOpen(g) ? g.tasks.map((t) => (
          <TaskTableRow key={t.id} task={t} columns={columns} expanded={expandedId === t.id} onToggle={onToggle}
            renderExpanded={renderExpanded} faded={isFaded(t)} ctx={contextOf(t)} testIdPrefix={testIdPrefix} />
        )) : []),
        isOpen(g) && g.tasks.length === 0 && g.emptyNote ? (
          <tr key={`e-${g.key}`}>
            <td colSpan={columns.length} className="px-3 py-3 text-sm text-gray-500 dark:text-gray-400">{g.emptyNote}</td>
          </tr>
        ) : null,
      ].filter(Boolean))}
    </PageScrollTable>
  );
}
